/**
 * useTimelineScrollAnchoring - owns where the chat timeline sits.
 *
 * Four questions used to be answered by twelve refs scattered through
 * ChatView's body: is the timeline following the live edge, is an anchor
 * scroll in flight, has the user opted out of stick-to-bottom, and where
 * should the list land when a new turn arrives. Every writer touched the raw
 * refs, so each new caller had to re-learn the whole protocol.
 *
 * They are one thing, and this module owns it. Callers get named operations —
 * a turn is going out, the user navigated, a queued prompt was promoted — and
 * never see the refs behind them.
 *
 * @module useTimelineScrollAnchoring
 */
import type { MessageId } from "@afar-research/contracts";
import { CHAT_LIST_ANCHOR_OFFSET } from "@afar-research/shared/chatList";
import { Debouncer } from "@tanstack/react-pacer";
import { type LegendListRef } from "@legendapp/list/react";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import {
  getAnchoredTurnMetrics,
  timelineContentOverflowsViewport,
  type TimelineScrollMode,
} from "./timelineScrollAnchoring";
import { decideTimelineFollowScroll } from "./timelineScrollPolicy";

export interface TimelineScrollAnchoringInput {
  /** The live timeline list. Owned by the caller, which renders it. */
  readonly listRef: RefObject<LegendListRef | null>;
  /** The list's end padding reserved for the composer. */
  readonly composerInset: number;
  readonly activeThreadId: string | null;
  /** Re-runs live-follow when the timeline gains or loses entries. */
  readonly timelineEntries: readonly unknown[];
}

export interface TimelineScrollAnchoring {
  readonly followsLiveEdge: boolean;
  readonly showScrollToBottom: boolean;
  readonly scrollToEnd: (animated?: boolean) => void;
  readonly onAnchorReady: (messageId: MessageId, anchorIndex: number) => void;
  readonly onAnchorSizeChanged: (messageId: MessageId) => void;
  readonly onIsAtEndChange: (isAtEnd: boolean) => void;
  readonly cancelLiveFollowForUserNavigation: () => void;
  /**
   * A turn is being dispatched. Keep the user's scroll position: if they were
   * already following the live edge, stay there; otherwise leave free-scrolling
   * alone (no jump-to-top).
   */
  readonly keepPositionForOutgoingTurn: () => void;
  /**
   * FIFO auto-drain promoted a queued prompt to active, so its bubble leaves
   * the pending stack and appears as the newest timeline user message. Place it
   * exactly as a normal turn start would.
   */
  readonly releaseAnchorForPromotedQueueEntry: () => void;
}

export function useTimelineScrollAnchoring(
  input: TimelineScrollAnchoringInput,
): TimelineScrollAnchoring {
  const { listRef, composerInset, activeThreadId, timelineEntries } = input;

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // Debounce *showing* the scroll-to-bottom pill so it doesn't flash during
  // thread switches. LegendList fires scroll events with isAtEnd=false while
  // initialScrollAtEnd is settling; hiding is always immediate.
  const showScrollDebouncer = useRef(
    new Debouncer(() => setShowScrollToBottom(true), { wait: 150 }),
  );
  const isAtEndRef = useRef(true);
  const timelineScrollModeRef = useRef<TimelineScrollMode>("following-end");
  const [timelineFollowsLiveEdge, setTimelineFollowsLiveEdge] = useState(true);
  const pendingTimelineAnchorRef = useRef<MessageId | null>(null);
  const positionedTimelineAnchorRef = useRef<MessageId | null>(null);
  const settledTimelineAnchorRef = useRef<MessageId | null>(null);
  const activeTimelineAnchorIndexRef = useRef<number | null>(null);
  const anchorUserScrollGenerationRef = useRef(0);
  const liveFollowUserScrollGenerationRef = useRef<number | null>(0);
  const pendingAnchorScrollRestoreRef = useRef<{
    readonly messageId: MessageId;
    readonly offset: number;
    readonly userScrollGeneration: number;
  } | null>(null);
  const anchorScrollRestoreFrameRef = useRef<number | null>(null);
  const anchorScrollInFlightRef = useRef(false);

  const cancelLiveFollowForUserNavigation = useCallback(() => {
    anchorUserScrollGenerationRef.current += 1;
    timelineScrollModeRef.current = "free-scrolling";
    setTimelineFollowsLiveEdge(false);
    liveFollowUserScrollGenerationRef.current = null;
    pendingTimelineAnchorRef.current = null;
    positionedTimelineAnchorRef.current = null;
    settledTimelineAnchorRef.current = null;
    activeTimelineAnchorIndexRef.current = null;
    pendingAnchorScrollRestoreRef.current = null;
    anchorScrollInFlightRef.current = false;
    if (anchorScrollRestoreFrameRef.current !== null) {
      cancelAnimationFrame(anchorScrollRestoreFrameRef.current);
      anchorScrollRestoreFrameRef.current = null;
    }
  }, []);
  const cancelLiveFollowForUserNavigationRef = useRef(cancelLiveFollowForUserNavigation);
  useEffect(() => {
    cancelLiveFollowForUserNavigationRef.current = cancelLiveFollowForUserNavigation;
  }, [cancelLiveFollowForUserNavigation]);

  const getActiveTimelineTurnMetrics = useCallback(
    (list?: LegendListRef | null) => {
      const resolvedList = list ?? listRef.current;
      const anchorIndex = activeTimelineAnchorIndexRef.current;
      const state = resolvedList?.getState();
      if (!resolvedList || !state || anchorIndex === null) {
        return null;
      }

      return getAnchoredTurnMetrics({
        state,
        anchorIndex,
        composerOverlayHeight: composerInset,
        anchorOffset: CHAT_LIST_ANCHOR_OFFSET,
      });
    },
    [composerInset, listRef],
  );
  const timelineRealContentOverflowsViewport = useCallback(
    (list?: LegendListRef | null) =>
      timelineContentOverflowsViewport((list ?? listRef.current)?.getState(), {
        composerInset,
        anchorOffset: CHAT_LIST_ANCHOR_OFFSET,
      }),
    [composerInset, listRef],
  );

  // Live-follow stays active after send/thread-open until an actual list scroll
  // gesture opts out.
  const scrollToEnd = useCallback(
    (animated = false) => {
      isAtEndRef.current = true;
      timelineScrollModeRef.current = "following-end";
      setTimelineFollowsLiveEdge(true);
      liveFollowUserScrollGenerationRef.current = anchorUserScrollGenerationRef.current;
      pendingTimelineAnchorRef.current = null;
      activeTimelineAnchorIndexRef.current = null;
      showScrollDebouncer.current.cancel();
      setShowScrollToBottom(false);
      void listRef.current?.scrollToEnd?.({ animated });
    },
    [listRef],
  );

  useEffect(() => {
    let removeListeners: (() => void) | null = null;
    const frame = requestAnimationFrame(() => {
      const scrollNode = listRef.current?.getScrollableNode();
      if (!scrollNode) {
        return;
      }
      const handleManualNavigation = () => {
        cancelLiveFollowForUserNavigationRef.current();
      };
      scrollNode.addEventListener("wheel", handleManualNavigation, {
        passive: true,
      });
      scrollNode.addEventListener("touchmove", handleManualNavigation, {
        passive: true,
      });
      scrollNode.addEventListener("pointerdown", handleManualNavigation, {
        passive: true,
      });
      removeListeners = () => {
        scrollNode.removeEventListener("wheel", handleManualNavigation);
        scrollNode.removeEventListener("touchmove", handleManualNavigation);
        scrollNode.removeEventListener("pointerdown", handleManualNavigation);
      };
    });

    return () => {
      cancelAnimationFrame(frame);
      removeListeners?.();
    };
  }, [activeThreadId, listRef]);

  const onAnchorReady = useCallback(
    (messageId: MessageId, anchorIndex: number) => {
      if (pendingTimelineAnchorRef.current === messageId) {
        pendingTimelineAnchorRef.current = null;
      }
      activeTimelineAnchorIndexRef.current = anchorIndex;
      if (positionedTimelineAnchorRef.current === messageId) {
        return;
      }
      positionedTimelineAnchorRef.current = messageId;
      settledTimelineAnchorRef.current = null;
      const positionAnchor = (remainingAttempts: number) => {
        requestAnimationFrame(() => {
          if (positionedTimelineAnchorRef.current !== messageId) {
            return;
          }
          const list = listRef.current;
          if (!list) {
            if (remainingAttempts > 0) {
              positionAnchor(remainingAttempts - 1);
            }
            return;
          }
          const scrollNode = list.getScrollableNode();
          let finished = false;
          const finishAnimatedPositioning = () => {
            if (finished) {
              return;
            }
            finished = true;
            window.clearTimeout(fallbackTimer);
            scrollNode.removeEventListener("scrollend", finishAnimatedPositioning);
            if (positionedTimelineAnchorRef.current !== messageId) {
              return;
            }
            const scrollOffset = list.getState().scroll;
            void list.scrollToOffset({ offset: scrollOffset, animated: false });
            settledTimelineAnchorRef.current = messageId;
            anchorScrollInFlightRef.current = false;
          };
          const fallbackTimer = window.setTimeout(finishAnimatedPositioning, 750);
          scrollNode.addEventListener("scrollend", finishAnimatedPositioning, { once: true });
          anchorScrollInFlightRef.current = true;
          void list.scrollToIndex({
            index: anchorIndex,
            animated: true,
            viewPosition: 0,
            viewOffset: CHAT_LIST_ANCHOR_OFFSET,
          });
        });
      };
      requestAnimationFrame(() => positionAnchor(12));
    },
    [listRef],
  );

  const onAnchorSizeChanged = useCallback(
    (messageId: MessageId) => {
      if (anchorScrollInFlightRef.current) {
        return;
      }
      if (settledTimelineAnchorRef.current !== messageId) {
        return;
      }
      if (liveFollowUserScrollGenerationRef.current === anchorUserScrollGenerationRef.current) {
        return;
      }
      const scrollOffset = listRef.current?.getState().scroll;
      if (scrollOffset === undefined) {
        return;
      }
      if (pendingAnchorScrollRestoreRef.current === null) {
        pendingAnchorScrollRestoreRef.current = {
          messageId,
          offset: scrollOffset,
          userScrollGeneration: anchorUserScrollGenerationRef.current,
        };
      }
      if (anchorScrollRestoreFrameRef.current !== null) {
        return;
      }
      anchorScrollRestoreFrameRef.current = requestAnimationFrame(() => {
        anchorScrollRestoreFrameRef.current = null;
        const pending = pendingAnchorScrollRestoreRef.current;
        pendingAnchorScrollRestoreRef.current = null;
        if (
          pending &&
          settledTimelineAnchorRef.current === pending.messageId &&
          pending.userScrollGeneration === anchorUserScrollGenerationRef.current
        ) {
          const list = listRef.current;
          const currentScrollOffset = list?.getState().scroll;
          if (
            typeof currentScrollOffset === "number" &&
            Math.abs(currentScrollOffset - pending.offset) <= 2
          ) {
            void list?.scrollToOffset({ offset: pending.offset, animated: false });
          }
        }
      });
    },
    [listRef],
  );

  const onIsAtEndChange = useCallback((isAtEnd: boolean) => {
    // Ignore false-positive live-edge hits while the user has opted out of
    // stick-to-bottom; otherwise we re-enter following-end and force scroll.
    if (isAtEnd && liveFollowUserScrollGenerationRef.current === null) {
      return;
    }
    if (
      !isAtEnd &&
      liveFollowUserScrollGenerationRef.current === anchorUserScrollGenerationRef.current
    ) {
      showScrollDebouncer.current.cancel();
      setShowScrollToBottom(false);
      return;
    }
    if (isAtEndRef.current === isAtEnd) return;
    isAtEndRef.current = isAtEnd;
    if (isAtEnd) {
      timelineScrollModeRef.current = "following-end";
      setTimelineFollowsLiveEdge(true);
      liveFollowUserScrollGenerationRef.current = anchorUserScrollGenerationRef.current;
      showScrollDebouncer.current.cancel();
      setShowScrollToBottom(false);
    } else {
      timelineScrollModeRef.current = "free-scrolling";
      setTimelineFollowsLiveEdge(false);
      liveFollowUserScrollGenerationRef.current = null;
      showScrollDebouncer.current.maybeExecute();
    }
  }, []);

  useEffect(() => {
    if (!activeThreadId) {
      return;
    }
    if (liveFollowUserScrollGenerationRef.current !== anchorUserScrollGenerationRef.current) {
      return;
    }
    if (timelineScrollModeRef.current === "free-scrolling") {
      return;
    }

    let secondFrame: number | null = null;
    const frame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (liveFollowUserScrollGenerationRef.current !== anchorUserScrollGenerationRef.current) {
          return;
        }
        if (timelineScrollModeRef.current === "free-scrolling") {
          return;
        }
        if (pendingTimelineAnchorRef.current !== null) {
          return;
        }
        if (
          positionedTimelineAnchorRef.current !== null &&
          settledTimelineAnchorRef.current !== positionedTimelineAnchorRef.current
        ) {
          return;
        }
        const list = listRef.current;
        if (!list) {
          return;
        }

        const follow = decideTimelineFollowScroll({
          mode: timelineScrollModeRef.current,
          anchorScrollInFlight: anchorScrollInFlightRef.current,
          scrollDeltaToRevealEnd:
            timelineScrollModeRef.current === "anchoring-new-turn"
              ? (getActiveTimelineTurnMetrics(list)?.scrollDeltaToRevealEnd ?? 0)
              : 0,
          realContentOverflowsViewport:
            timelineScrollModeRef.current === "following-end"
              ? timelineRealContentOverflowsViewport(list)
              : false,
        });
        if (follow.type === "scrollToOffset") {
          const metrics = getActiveTimelineTurnMetrics(list);
          if (!metrics || metrics.scrollDeltaToRevealEnd <= 1) {
            return;
          }
          const nextOffset = list.getState().scroll + metrics.scrollDeltaToRevealEnd;
          void list.scrollToOffset({ offset: nextOffset, animated: false });
          return;
        }
        if (follow.type === "scrollToEnd") {
          void list.scrollToEnd?.({ animated: false });
        }
      });
    });

    return () => {
      cancelAnimationFrame(frame);
      if (secondFrame !== null) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [
    activeThreadId,
    timelineEntries,
    getActiveTimelineTurnMetrics,
    timelineRealContentOverflowsViewport,
    listRef,
  ]);

  const keepPositionForOutgoingTurn = useCallback(() => {
    if (timelineScrollModeRef.current !== "free-scrolling" && isAtEndRef.current) {
      timelineScrollModeRef.current = "following-end";
      setTimelineFollowsLiveEdge(true);
      showScrollDebouncer.current.cancel();
      setShowScrollToBottom(false);
    }
    pendingTimelineAnchorRef.current = null;
    activeTimelineAnchorIndexRef.current = null;
  }, []);

  const releaseAnchorForPromotedQueueEntry = useCallback(() => {
    keepPositionForOutgoingTurn();
    positionedTimelineAnchorRef.current = null;
    settledTimelineAnchorRef.current = null;
  }, [keepPositionForOutgoingTurn]);

  useEffect(() => {
    isAtEndRef.current = true;
    timelineScrollModeRef.current = "following-end";
    setTimelineFollowsLiveEdge(true);
    liveFollowUserScrollGenerationRef.current = anchorUserScrollGenerationRef.current;
    pendingTimelineAnchorRef.current = null;
    positionedTimelineAnchorRef.current = null;
    settledTimelineAnchorRef.current = null;
    activeTimelineAnchorIndexRef.current = null;
    showScrollDebouncer.current.cancel();
    setShowScrollToBottom(false);
  }, [activeThreadId]);

  return {
    followsLiveEdge: timelineFollowsLiveEdge,
    showScrollToBottom,
    scrollToEnd,
    onAnchorReady,
    onAnchorSizeChanged,
    onIsAtEndChange,
    cancelLiveFollowForUserNavigation,
    keepPositionForOutgoingTurn,
    releaseAnchorForPromotedQueueEntry,
  };
}
